import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Check, X, Clock, Trash2, CalendarOff, Timer, ClipboardList, UserCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import HoursBatchEntryDialog from "@/components/employees/tabs/HoursBatchEntryDialog";
import SubmissionEditDialog from "@/components/submissions/SubmissionEditDialog";

const ABSENCE_LABELS = {
  cp: "Congés payés",
  rtt_salarie: "RTT salarié",
  rtt_entreprise: "RTT entreprise",
  sickness: "Maladie",
  other: "Autre",
};

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

/**
 * Parse une semaine ISO "YYYY-Www" et renvoie {start: lundi, end: dimanche} en YYYY-MM-DD.
 */
const isoWeekRange = (iso) => {
  if (!iso) return null;
  const m = /^(\d{4})-W(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  // ISO 8601: jeudi de la semaine 1 est dans la première semaine contenant le 4 janvier.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = (jan4.getUTCDay() + 6) % 7; // Lun=0
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day);
  const start = new Date(week1Monday);
  start.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const EmployeeSubmissionsPage = () => {
  const { user } = useAuth();
  // Compte ouvrier (ou tout compte lié à un salarié) : il ne peut soumettre QUE pour
  // son propre salarié. On masque le picker et on auto-sélectionne son employee_id.
  const linkedEmployeeId = user?.employee_id || null;
  const restrictedMode = !!linkedEmployeeId && user?.role !== "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ items: [], counts: {} });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("hours");
  const [statusFilter, setStatusFilter] = useState("pending");
  // Hours submit
  const [hoursDialogEmp, setHoursDialogEmp] = useState(null);
  const [hoursPrefill, setHoursPrefill] = useState(null);
  const [qrBanner, setQrBanner] = useState(null);
  // Picker (étape 1 : choisir le salarié avant d'ouvrir HoursBatchEntryDialog)
  const [hoursPickerOpen, setHoursPickerOpen] = useState(false);
  const [hoursPickerEmpId, setHoursPickerEmpId] = useState("");
  // Absence submit
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const [absForm, setAbsForm] = useState({ employee_id: "", absence_type: "cp", start_date: "", end_date: "", is_half_day: false, half_day_period: null, start_time: "", end_time: "", reason: "" });
  // Approve dialog
  const [approveDialog, setApproveDialog] = useState(null);
  // Signature viewer
  const [signatureViewer, setSignatureViewer] = useState(null);
  // Edit submission (admin)
  const [editSubmission, setEditSubmission] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const subs = await api.getEmployeeSubmissions();
      setData(subs.data);
      // En mode restreint (ouvrier), pas d'appel /api/employees (permission denied + sécurité).
      if (restrictedMode) {
        setEmployees([]);
      } else {
        const emp = await api.getEmployees();
        setEmployees((emp.data || []).filter((e) => !e.archived && !e.exit_date));
      }
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  // Consomme les query params du QR code chantier (prefill_order + week)
  useEffect(() => {
    const orderId = searchParams.get("prefill_order");
    const week = searchParams.get("week");
    if (!orderId) return;
    const range = isoWeekRange(week);
    if (!range) return;
    // Salarié à utiliser : ouvrier → son propre employee_id ; sinon → 1er actif chargé.
    let emp = null;
    if (restrictedMode && linkedEmployeeId) {
      emp = { id: linkedEmployeeId, first_name: "Moi", last_name: "" };
    } else if (employees.length > 0) {
      emp = employees[0];
    } else {
      return; // attendre que les employés soient chargés
    }
    setHoursPrefill({ orderId, startDate: range.start, endDate: range.end });
    setHoursDialogEmp(emp);
    setQrBanner({ orderId, week });
    // Nettoie les params pour éviter de re-déclencher
    const next = new URLSearchParams(searchParams);
    next.delete("prefill_order");
    next.delete("week");
    setSearchParams(next, { replace: true });
  }, [employees, searchParams, setSearchParams, restrictedMode, linkedEmployeeId]);

  const items = (data.items || []).filter((i) => i.type === activeTab && (statusFilter === "all" || i.status === statusFilter));

  const handleSubmitAbsence = async () => {
    if (!absForm.employee_id || !absForm.start_date || !absForm.end_date) {
      return toast.error("Salarié et dates obligatoires");
    }
    try {
      setSaving(true);
      await api.submitAbsenceRequest(absForm);
      toast.success("Demande d'absence soumise");
      setAbsenceOpen(false);
      setAbsForm({ employee_id: "", absence_type: "cp", start_date: "", end_date: "", is_half_day: false, half_day_period: null, start_time: "", end_time: "", reason: "" });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (sub, deductBalance = null) => {
    try {
      const body = sub.type === "absence" ? { deduct_balance: deductBalance } : {};
      const res = await api.approveSubmission(sub.id, body);
      toast.success(sub.type === "hours"
        ? `${res.data.created_refs_count} entrée(s) créée(s)`
        : deductBalance ? "Absence validée + solde déduit" : "Absence validée");
      setApproveDialog(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const handleReject = async (sub) => {
    const notes = prompt("Raison du refus (optionnel)");
    if (notes === null) return;
    try {
      await api.rejectSubmission(sub.id, { admin_notes: notes });
      toast.success("Demande refusée");
      fetchData();
    } catch {
      toast.error("Échec");
    }
  };

  const handleDelete = async (sub) => {
    if (!confirm("Supprimer cette demande ?")) return;
    await api.deleteSubmission(sub.id);
    toast.success("Supprimée");
    fetchData();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-indigo-600" />Fiches salariés
          </h1>
          <p className="text-sm text-muted-foreground">Déclarations d'heures et demandes de congés à valider</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setAbsForm({
                employee_id: restrictedMode ? linkedEmployeeId : "",
                absence_type: "cp",
                start_date: "",
                end_date: "",
                is_half_day: false,
                half_day_period: null,
                start_time: "",
                end_time: "",
                reason: "",
              });
              setAbsenceOpen(true);
            }}
            data-testid="new-absence-submission"
          >
            <CalendarOff className="h-4 w-4 mr-1" />Demande d'absence
          </Button>
          <Button
            onClick={() => {
              if (restrictedMode) {
                const myEmp = employees.find((e) => e.id === linkedEmployeeId)
                  || { id: linkedEmployeeId, first_name: "Moi", last_name: "" };
                setHoursDialogEmp(myEmp);
                return;
              }
              if (employees.length === 0) return toast.error("Aucun salarié actif");
              setHoursPickerEmpId("");
              setHoursPickerOpen(true);
            }}
            data-testid="new-hours-submission"
          >
            <Plus className="h-4 w-4 mr-1" />Saisir des heures
          </Button>
        </div>
      </div>

      {qrBanner && (
        <div
          className="rounded-lg border-l-4 border-l-indigo-500 bg-indigo-50 p-3 flex items-center gap-3"
          data-testid="qr-banner"
        >
          <div className="h-9 w-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold">
            QR
          </div>
          <div className="flex-1 text-sm">
            Pointage chantier détecté — saisie pré-remplie pour la commande
            <strong className="ml-1">{qrBanner.orderId.slice(0, 8)}…</strong> sur la semaine
            <strong className="ml-1">{qrBanner.week}</strong>.
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-indigo-200">
          <CardContent className="pt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-indigo-100 rounded-sm flex items-center justify-center"><Timer className="h-5 w-5 text-indigo-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">Heures en attente</div>
              <div className="text-2xl font-bold">{data.counts?.pending_hours || 0}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-amber-100 rounded-sm flex items-center justify-center"><CalendarOff className="h-5 w-5 text-amber-600" /></div>
            <div>
              <div className="text-xs text-muted-foreground">Absences en attente</div>
              <div className="text-2xl font-bold">{data.counts?.pending_absences || 0}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="hours" data-testid="subs-tab-hours">Heures</TabsTrigger>
            <TabsTrigger value="absence" data-testid="subs-tab-absences">Absences</TabsTrigger>
          </TabsList>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="approved">Approuvées</SelectItem>
              <SelectItem value="rejected">Refusées</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <TabsContent value={activeTab} className="mt-3">
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : items.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune demande</p>
              ) : (
                <div className="space-y-2">
                  {items.map((s) => (
                    <div key={s.id} className="rounded-lg border p-3 flex flex-wrap items-center gap-3" data-testid={`submission-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{s.employee_name}</span>
                          <Badge variant="outline" className={STATUS_BADGE[s.status]}>
                            {s.status === "pending" ? "En attente" : s.status === "approved" ? "Approuvée" : "Refusée"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            <Clock className="h-3 w-3 inline mr-0.5" />{s.created_at?.slice(0, 10)}
                          </span>
                        </div>
                        <div className="text-sm mt-1">
                          {s.type === "hours" ? (
                            <span>
                              {(s.payload?.entries || []).length} tranche(s) sur {
                                new Set((s.payload?.entries || []).map(e => e.date)).size
                              } jour(s)
                              {s.payload?.notes && <span className="text-muted-foreground"> — {s.payload.notes}</span>}
                              {s.payload?.signature && (
                                <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 text-xs" title={`Signé le ${s.payload.signed_at?.slice(0,10) || "—"}`}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                  Signée
                                </span>
                              )}
                            </span>
                          ) : (
                            <span>
                              {ABSENCE_LABELS[s.payload?.absence_type] || s.payload?.absence_type}
                              {" — "}
                              {s.payload?.start_date} → {s.payload?.end_date}
                              {s.payload?.is_half_day && " (demi-journée)"}
                              {s.payload?.reason && <span className="text-muted-foreground"> — {s.payload.reason}</span>}
                            </span>
                          )}
                        </div>
                        {s.admin_notes && <div className="text-xs text-slate-600 italic mt-1">Note admin: {s.admin_notes}</div>}
                        {(s.submitted_by_name || s.submitted_by_email) && (
                          <div
                            className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"
                            data-testid={`submitted-by-${s.id}`}
                          >
                            <UserCircle2 className="h-3 w-3" />
                            Soumise par {s.submitted_by_name || s.submitted_by_email}
                          </div>
                        )}
                      </div>
                      {s.type === "hours" && s.payload?.signature && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                          onClick={() => setSignatureViewer(s)}
                          data-testid={`view-signature-${s.id}`}
                        >
                          Signature
                        </Button>
                      )}
                      {s.type === "hours" && data.is_admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-slate-700 border-slate-300 hover:bg-slate-50"
                          onClick={() => setEditSubmission(s)}
                          data-testid={`edit-submission-${s.id}`}
                          title={s.status === "approved" ? "Modifier (réécrit les heures du planning)" : "Modifier avant validation"}
                        >
                          <Pencil className="h-4 w-4 mr-1" />Modifier
                        </Button>
                      )}
                      {s.status === "pending" && data.is_admin && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => s.type === "absence" ? setApproveDialog(s) : handleApprove(s)}
                            data-testid={`approve-${s.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />Approuver
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleReject(s)} data-testid={`reject-${s.id}`}>
                            <X className="h-4 w-4 mr-1" />Refuser
                          </Button>
                        </div>
                      )}
                      {(data.is_admin || (s.status === "pending" && s.submitted_by_name)) && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(s)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Signature viewer */}
      <Dialog open={!!signatureViewer} onOpenChange={(v) => !v && setSignatureViewer(null)}>
        <DialogContent data-testid="signature-viewer-dialog">
          <DialogHeader>
            <DialogTitle>Signature — {signatureViewer?.employee_name}</DialogTitle>
            <DialogDescription>
              Signée le {signatureViewer?.payload?.signed_at?.slice(0, 19).replace("T", " ") || "—"}
            </DialogDescription>
          </DialogHeader>
          {signatureViewer?.payload?.signature && (
            <img
              src={signatureViewer.payload.signature}
              alt="Signature"
              className="w-full border rounded bg-white"
              data-testid="signature-image"
            />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSignatureViewer(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve with deduct_balance choice (for absences) */}
      <Dialog open={!!approveDialog} onOpenChange={(v) => !v && setApproveDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="approve-absence-dialog">
          <DialogHeader>
            <DialogTitle>Approuver la demande d&apos;absence</DialogTitle>
            <DialogDescription>
              Souhaitez-vous déduire cette absence du solde {
                approveDialog?.payload?.absence_type === "cp" ? "de congés payés" :
                approveDialog?.payload?.absence_type === "rtt_salarie" ? "RTT salarié" :
                approveDialog?.payload?.absence_type === "rtt_entreprise" ? "RTT entreprise" : ""
              } de {approveDialog?.employee_name} ?
              <br />
              Période : {approveDialog?.payload?.start_date} → {approveDialog?.payload?.end_date}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setApproveDialog(null)} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button
              onClick={() => handleApprove(approveDialog, true)}
              data-testid="approve-with-deduct"
              className="w-full sm:w-auto"
            >
              Approuver + déduire le solde
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Absence submission dialog */}
      <Dialog open={absenceOpen} onOpenChange={setAbsenceOpen}>
        <DialogContent data-testid="absence-submit-dialog">
          <DialogHeader>
            <DialogTitle>Demande d'absence / RTT / congés</DialogTitle>
            <DialogDescription>Cette demande sera soumise à validation par l'administrateur.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {!restrictedMode && (
              <div className="col-span-2">
                <Label>Salarié *</Label>
                <Select value={absForm.employee_id} onValueChange={(v) => setAbsForm({ ...absForm, employee_id: v })}>
                  <SelectTrigger data-testid="abs-employee"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Type</Label>
              <Select value={absForm.absence_type} onValueChange={(v) => setAbsForm({ ...absForm, absence_type: v })}>
                <SelectTrigger data-testid="abs-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ABSENCE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Demi-journée</Label>
              <Select value={absForm.is_half_day ? "yes" : "no"} onValueChange={(v) => setAbsForm({ ...absForm, is_half_day: v === "yes", half_day_period: v === "yes" ? (absForm.half_day_period || "morning") : null })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">Journée complète</SelectItem>
                  <SelectItem value="yes">Demi-journée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {absForm.is_half_day && (
              <div className="col-span-2 flex items-center gap-4 pl-1">
                <Label className="text-sm">Période :</Label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="abs-period" checked={absForm.half_day_period === "morning"} onChange={() => setAbsForm({ ...absForm, half_day_period: "morning" })} data-testid="abs-half-morning" /> Matin
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="abs-period" checked={absForm.half_day_period === "afternoon"} onChange={() => setAbsForm({ ...absForm, half_day_period: "afternoon" })} data-testid="abs-half-afternoon" /> Après-midi
                </label>
              </div>
            )}
            <div>
              <Label>Date de début *</Label>
              <Input type="date" value={absForm.start_date} onChange={(e) => setAbsForm({ ...absForm, start_date: e.target.value })} data-testid="abs-start" />
            </div>
            <div>
              <Label>Date de fin *</Label>
              <Input type="date" value={absForm.end_date} onChange={(e) => setAbsForm({ ...absForm, end_date: e.target.value })} data-testid="abs-end" />
            </div>
            {["intemperies", "justifiee", "injustifiee"].includes(absForm.absence_type) && (
              <>
                <div>
                  <Label>Heure début (optionnel)</Label>
                  <Input type="time" value={absForm.start_time || ""} onChange={(e) => setAbsForm({ ...absForm, start_time: e.target.value })} data-testid="abs-start-time" />
                </div>
                <div>
                  <Label>Heure fin (optionnel)</Label>
                  <Input type="time" value={absForm.end_time || ""} onChange={(e) => setAbsForm({ ...absForm, end_time: e.target.value })} data-testid="abs-end-time" />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label>Motif (optionnel)</Label>
              <Textarea rows={2} value={absForm.reason} onChange={(e) => setAbsForm({ ...absForm, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAbsenceOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={handleSubmitAbsence} disabled={saving} data-testid="submit-absence-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Soumettre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hours employee picker (étape 1) */}
      <Dialog open={hoursPickerOpen} onOpenChange={setHoursPickerOpen}>
        <DialogContent data-testid="hours-picker-dialog">
          <DialogHeader>
            <DialogTitle>Saisir des heures pour…</DialogTitle>
            <DialogDescription>
              Choisissez le salarié dont vous souhaitez saisir les heures. La fiche sera ensuite
              soumise à validation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Salarié *</Label>
            <Select value={hoursPickerEmpId} onValueChange={setHoursPickerEmpId}>
              <SelectTrigger data-testid="hours-picker-employee">
                <SelectValue placeholder="Choisir un salarié" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.first_name} {e.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHoursPickerOpen(false)}>Annuler</Button>
            <Button
              onClick={() => {
                const emp = employees.find((e) => e.id === hoursPickerEmpId);
                if (!emp) return toast.error("Sélectionnez un salarié");
                setHoursPickerOpen(false);
                setHoursDialogEmp(emp);
              }}
              data-testid="hours-picker-continue"
            >
              Continuer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hours submission dialog — réutilise le composant existant, mais route vers submit-queue */}
      {hoursDialogEmp && (
        <HoursBatchEntryDialog
          open={!!hoursDialogEmp}
          onOpenChange={(v) => {
            if (!v) {
              setHoursDialogEmp(null);
              setHoursPrefill(null);
              setQrBanner(null);
            }
          }}
          employee={hoursDialogEmp}
          submitAsSubmission={true}
          prefill={hoursPrefill}
          onSaved={() => { setHoursDialogEmp(null); setHoursPrefill(null); setQrBanner(null); fetchData(); }}
        />
      )}

      {/* Admin: éditer une fiche d'heures (pending ou approved) */}
      <SubmissionEditDialog
        open={!!editSubmission}
        submission={editSubmission}
        onClose={() => setEditSubmission(null)}
        onSaved={() => { setEditSubmission(null); fetchData(); }}
      />
    </div>
  );
};

export default EmployeeSubmissionsPage;
